// Pure board / list logic shared by the status board, the bucket board and the space overview
// (columns, grouping, filters, drop and reorder computation). No DOM / React here: unit-testable.
import { STATUS_CATEGORIES, type Bucket, type Item, type Status, type StatusCategory } from "../api/types";
import { CATEGORY_LABELS, isClosed } from "./format";

/** Column key of the "No bucket" column; also the group key in lists. */
export const NO_BUCKET = "__none__";
/** Droppable id prefix of a column (cards use their item id). */
export const COL_PREFIX = "col:";

export type Columns = Record<string, string[]>;

export function byPosition(a: Item, b: Item): number {
  if (a.position < b.position) return -1;
  if (a.position > b.position) return 1;
  return a.created_at < b.created_at ? -1 : 1;
}

/** Cards on boards and rows in lists: top-level, no meetings. */
export function isBoardItem(it: Item): boolean {
  return !it.parent_id && it.type !== "meeting";
}

// ---------------------------------------------------------------------------
// Columns

/** Status categories always shown on the space board; the others only when they have cards. */
const ALWAYS_SHOWN: StatusCategory[] = ["todo", "in_progress", "done"];

/** Space board: one column per status category (keys = categories, in canonical order). */
export function categoryColumns(items: Item[]): { keys: StatusCategory[]; columns: Columns } {
  const columns: Columns = {};
  for (const c of STATUS_CATEGORIES) columns[c] = [];
  for (const it of [...items].sort(byPosition)) if (isBoardItem(it)) columns[it.status_category]?.push(it.id);
  const keys = STATUS_CATEGORIES.filter((c) => ALWAYS_SHOWN.includes(c) || columns[c].length > 0);
  return { keys, columns };
}

/**
 * Bucket board: one column per bucket (by position) plus a leading "No bucket" column, shown only
 * when some card has no bucket (or one that no longer exists).
 */
export function bucketColumns(buckets: Bucket[], items: Item[]): { keys: string[]; columns: Columns } {
  const ordered = [...buckets].sort((a, b) => a.position - b.position);
  const columns: Columns = {};
  for (const b of ordered) columns[b.id] = [];
  const loose: string[] = [];
  for (const it of [...items].sort(byPosition)) {
    if (!isBoardItem(it)) continue;
    const list = it.bucket_id ? columns[it.bucket_id] : undefined;
    if (list) list.push(it.id);
    else loose.push(it.id);
  }
  const keys = ordered.map((b) => b.id);
  if (loose.length) {
    columns[NO_BUCKET] = loose;
    keys.unshift(NO_BUCKET);
  }
  return { keys, columns };
}

// ---------------------------------------------------------------------------
// Drag & drop

export function containerOf(id: string, cols: Columns): string | undefined {
  if (id.startsWith(COL_PREFIX)) return id.slice(COL_PREFIX.length);
  return Object.keys(cols).find((k) => cols[k].includes(id));
}

export function arrayMove<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
}

/** Live preview while dragging over another column: move the card into it (at the hovered card). */
export function dragOverColumns(cols: Columns, activeId: string, overId: string): Columns | null {
  const from = containerOf(activeId, cols);
  const to = containerOf(overId, cols);
  if (!from || !to || from === to || !cols[to]) return null;
  const toList = [...cols[to]];
  const overIndex = toList.indexOf(overId);
  toList.splice(overIndex >= 0 ? overIndex : toList.length, 0, activeId);
  return { ...cols, [from]: cols[from].filter((x) => x !== activeId), [to]: toList };
}

export interface CardDrop {
  container: string;
  /** Final order of the target column. */
  list: string[];
  index: number;
}

/**
 * Where a dragged card lands (after `dragOverColumns` moved it into the target column), or null
 * when nothing changed. `origin` is the column/index the card started in.
 */
export function cardDrop(
  cols: Columns,
  activeId: string,
  overId: string,
  origin: { container: string; index: number },
): CardDrop | null {
  const container = containerOf(activeId, cols);
  if (!container || container !== containerOf(overId, cols)) return null;
  let list = cols[container];
  const oldIndex = list.indexOf(activeId);
  const overIndex = overId.startsWith(COL_PREFIX) ? list.length - 1 : list.indexOf(overId);
  if (overIndex >= 0 && oldIndex !== overIndex) list = arrayMove(list, oldIndex, overIndex);
  const index = list.indexOf(activeId);
  if (container === origin.container && index === origin.index) return null;
  return { container, list, index };
}

/**
 * Move-API neighbours of position `index`: `after_id` = nearest card above, `before_id` = nearest
 * below. `eligible` skips cards that can't be siblings (space board: other collections).
 */
export function neighbours(
  list: string[],
  index: number,
  eligible: (id: string) => boolean = () => true,
): { afterId: string | null; beforeId: string | null } {
  let afterId: string | null = null;
  let beforeId: string | null = null;
  for (let i = index - 1; i >= 0; i--) {
    if (eligible(list[i])) {
      afterId = list[i];
      break;
    }
  }
  for (let i = index + 1; i < list.length; i++) {
    if (eligible(list[i])) {
      beforeId = list[i];
      break;
    }
  }
  return { afterId, beforeId };
}

/**
 * Column drag: bucket `activeId` dropped on column `overId` → its new position, or null when
 * the order doesn't change. The "No bucket" column is not a bucket and is ignored.
 */
export function bucketReorder(
  buckets: Bucket[],
  activeId: string,
  overId: string,
): { bucket: Bucket; position: number; order: string[] } | null {
  const ordered = [...buckets].sort((a, b) => a.position - b.position);
  const ids = ordered.map((b) => b.id);
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return null;
  // The server takes the target index and renumbers the other buckets 0..n-1.
  return { bucket: ordered[from], position: to, order: arrayMove(ids, from, to) };
}

/** Position for a new bucket appended at the end. */
export function nextBucketPosition(buckets: Bucket[]): number {
  return buckets.length ? Math.max(...buckets.map((b) => b.position)) + 1 : 0;
}

// ---------------------------------------------------------------------------
// Lists: grouping & filters

export type GroupMode = "status" | "bucket" | "collection";

export interface ItemGroup {
  key: string;
  title: string;
  /** Status groups: category (for the colored dot). */
  category?: StatusCategory;
  items: Item[];
  collapsedByDefault: boolean;
}

/** Open items before closed ones, then by position. */
function openFirst(a: Item, b: Item): number {
  return Number(isClosed(a.status_category)) - Number(isClosed(b.status_category)) || byPosition(a, b);
}

/** Group list rows (top-level, no meetings); empty groups are left out. */
export function groupItems(
  items: Item[],
  mode: GroupMode,
  ctx: { buckets?: Bucket[]; collections?: { id: string; name: string }[] } = {},
): ItemGroup[] {
  const rows = items.filter(isBoardItem);
  if (mode === "status") {
    return STATUS_CATEGORIES.map((cat) => ({
      key: cat,
      title: CATEGORY_LABELS[cat],
      category: cat,
      items: rows.filter((i) => i.status_category === cat).sort(byPosition),
      collapsedByDefault: isClosed(cat),
    })).filter((g) => g.items.length > 0);
  }
  const defs =
    mode === "bucket"
      ? [...(ctx.buckets ?? [])].sort((a, b) => a.position - b.position).map((b) => ({ key: b.id, title: b.name }))
      : (ctx.collections ?? []).map((c) => ({ key: c.id, title: c.name }));
  const keyOf = (i: Item) => (mode === "bucket" ? i.bucket_id : i.collection_id) ?? "";
  const known = new Set(defs.map((d) => d.key));
  const groups: ItemGroup[] = defs.map((d) => ({
    ...d,
    items: rows.filter((i) => keyOf(i) === d.key).sort(openFirst),
    collapsedByDefault: false,
  }));
  const rest = rows.filter((i) => !known.has(keyOf(i))).sort(openFirst);
  if (rest.length) {
    const g = { key: NO_BUCKET, title: mode === "bucket" ? "No bucket" : "Other", items: rest, collapsedByDefault: false };
    if (mode === "bucket") groups.unshift(g);
    else groups.push(g);
  }
  return groups.filter((g) => g.items.length > 0);
}

/** Space overview filters: tasks only, optionally without closed ones, matching all search words. */
export function filterItems(items: Item[], opts: { includeDone: boolean; q: string }): Item[] {
  const words = opts.q.toLowerCase().split(/\s+/).filter(Boolean);
  return items.filter((i) => {
    if (i.type !== "task") return false;
    if (!opts.includeDone && isClosed(i.status_category)) return false;
    if (!words.length) return true;
    const hay = `${i.title} ${i.labels.map((l) => `#${l}`).join(" ")} ${i.uid}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Default quick-add target: the saved one if still valid, else the first non-notebook collection. */
export function pickTargetCollection(
  collections: { id: string; kind: string }[],
  saved: string | null,
): string | null {
  const usable = collections.filter((c) => c.kind !== "notebook");
  return usable.find((c) => c.id === saved)?.id ?? usable[0]?.id ?? null;
}

/** First status (by position) of a category within a status scheme. */
export function statusForCategory(statuses: Status[], schemeId: string | undefined, category: StatusCategory): Status | undefined {
  return [...statuses]
    .filter((s) => s.category === category && (!schemeId || s.scheme_id === schemeId))
    .sort((a, b) => a.position - b.position)[0];
}
